import type { CSSProperties } from 'react';
import { symbolImage } from '@shared/symbols';
import { useT } from '../i18n';
import type { DisplayCategory } from '../lib/board';
import { Pictogram } from './Pictogram';

/** The first tab: "Frequentes" (the most used symbols). */
export const HOME = 'home';
export const ROUTINE = 'routine';
export const KEYBOARD = 'keyboard';

interface Props {
  categories: DisplayCategory[];
  current: string;
  onSelect(id: string): void;
  onNewCategory(): void;
  canEdit: boolean;
  /** Categories hidden from the child: left out, or shown faded while editing. */
  hiddenCategories: Set<string>;
  /** Show the "Rotina" tab (there is a routine, or the caregiver is editing). */
  showRoutine: boolean;
}

export function CategoryNav({ categories, current, onSelect, onNewCategory, canEdit, hiddenCategories, showRoutine }: Props) {
  const t = useT();
  const item = (id: string, icon: string, image: string | undefined, label: string, color = '#475569', hidden = false) => (
    <li key={id}>
      <button
        type="button"
        className={`cat${hidden ? ' cat--hidden' : ''}`}
        style={{ '--accent': color } as CSSProperties}
        aria-current={current === id ? 'page' : undefined}
        aria-label={hidden ? `${label} (${t('hiddenTag')})` : undefined}
        onClick={() => onSelect(id)}
      >
        <Pictogram source={{ icon, image }} className="cat__icon" />
        <span className="cat__label">
          {hidden && <span aria-hidden="true">🙈 </span>}
          {label}
        </span>
      </button>
    </li>
  );

  return (
    <nav className="cat-nav" aria-label={t('categoriesNav')}>
      <ul>
        {item(HOME, '⭐', symbolImage('cat-frequent.webp'), t('frequent'), '#1d4ed8')}
        {showRoutine && item(ROUTINE, '🗓️', symbolImage('cat-routine.webp'), t('routine'), '#0f766e')}
        {categories
          .filter((c) => canEdit || !hiddenCategories.has(c.id))
          .map((c) => item(c.id, c.icon, c.image, c.name, c.color, hiddenCategories.has(c.id)))}
        {item(KEYBOARD, '⌨️', symbolImage('cat-keyboard.webp'), t('keyboard'), '#475569')}
        {canEdit && (
          <li>
            <button type="button" className="cat cat--new" onClick={onNewCategory}>
              <span className="cat__icon" aria-hidden="true">
                ➕
              </span>
              <span className="cat__label">{t('newCategory')}</span>
            </button>
          </li>
        )}
      </ul>
    </nav>
  );
}
